/**
 * WeaveIn.ai Platform - Live Prediction Panel
 * On-demand ML prediction widget for member profile pages.
 * Shows real-time churn score, risk level, top contributing factors,
 * and prediction history trend chart.
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  AlertTriangle,
  Shield,
  Eye,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Brain,
} from 'lucide-react';
import { predictionsAPI, PredictionResult, PredictionHistoryEntry } from '@/services/api/predictions';

// ============================================================================
// RISK LEVEL DISPLAY CONFIG
// ============================================================================

const RISK_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
  healthy: { color: 'text-green-700', bg: 'bg-green-100', icon: <Shield className="w-4 h-4" />, label: 'Healthy' },
  watch: { color: 'text-yellow-700', bg: 'bg-yellow-100', icon: <Eye className="w-4 h-4" />, label: 'Watch' },
  at_risk: { color: 'text-orange-700', bg: 'bg-orange-100', icon: <AlertTriangle className="w-4 h-4" />, label: 'At Risk' },
  critical: { color: 'text-red-700', bg: 'bg-red-100', icon: <AlertTriangle className="w-4 h-4" />, label: 'Critical' },
  lost: { color: 'text-gray-700', bg: 'bg-gray-200', icon: <TrendingDown className="w-4 h-4" />, label: 'Lost' },
};

// ============================================================================
// SCORE GAUGE
// ============================================================================

const ScoreGauge: React.FC<{ score: number; size?: number }> = ({ score, size = 120 }) => {
  const percentage = Math.round(score * 100);
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score * circumference);
  const center = size / 2;

  // Color based on score
  const getColor = (s: number) => {
    if (s >= 0.7) return '#ef4444';  // red
    if (s >= 0.4) return '#f97316';  // orange
    if (s >= 0.25) return '#eab308'; // yellow
    return '#22c55e';                 // green
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={center} cy={center} r={radius}
          fill="none" stroke="#e5e7eb" strokeWidth="8"
        />
        <circle
          cx={center} cy={center} r={radius}
          fill="none" stroke={getColor(score)} strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold">{percentage}%</span>
        <span className="text-xs text-gray-500">churn risk</span>
      </div>
    </div>
  );
};

// ============================================================================
// SPARKLINE (mini trend chart)
// ============================================================================

const Sparkline: React.FC<{ data: number[]; width?: number; height?: number }> = ({
  data, width = 200, height = 40,
}) => {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padding = 2;

  const points = data.map((val, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((val - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke="#6366f1"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ============================================================================
// TOP FACTORS LIST
// ============================================================================

const TopFactors: React.FC<{ factors: PredictionResult['top_factors'] }> = ({ factors }) => {
  if (!factors || factors.length === 0) return null;

  const formatFeature = (name: string) => {
    return name
      .replace(/^f_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="space-y-1.5">
      {factors.slice(0, 5).map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-700">{formatFeature(f.feature)}</span>
              <span className="text-gray-500">{(f.importance * 100).toFixed(0)}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full mt-0.5">
              <div
                className="h-1.5 bg-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, f.importance * 100 * 3)}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface LivePredictionPanelProps {
  memberId: number;
  className?: string;
}

export const LivePredictionPanel: React.FC<LivePredictionPanelProps> = ({
  memberId, className = '',
}) => {
  const queryClient = useQueryClient();
  const [horizon, setHorizon] = useState<number>(30);
  const [showFactors, setShowFactors] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Fetch current prediction
  const { data: prediction, isLoading, error } = useQuery({
    queryKey: ['prediction', memberId, horizon],
    queryFn: () => predictionsAPI.getMemberPrediction(memberId, horizon),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch prediction history
  const { data: historyData } = useQuery({
    queryKey: ['prediction-history', memberId, horizon],
    queryFn: () => predictionsAPI.getMemberPredictionHistory(memberId, horizon),
    enabled: showHistory,
    staleTime: 10 * 60 * 1000,
  });

  // Refresh mutation
  const refreshMutation = useMutation({
    mutationFn: () => predictionsAPI.getMemberPrediction(memberId, horizon, 'churn', true),
    onSuccess: (data) => {
      queryClient.setQueryData(['prediction', memberId, horizon], data);
    },
  });

  const riskConfig = prediction ? RISK_CONFIG[prediction.risk_level] || RISK_CONFIG.watch : null;

  if (isLoading) {
    return (
      <div className={`bg-white border rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <Brain className="w-4 h-4 animate-pulse" />
          <span className="text-sm">Computing prediction...</span>
        </div>
      </div>
    );
  }

  if (error || !prediction) {
    return (
      <div className={`bg-white border rounded-lg p-4 ${className}`}>
        <div className="text-sm text-gray-500">Unable to generate prediction</div>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">ML Churn Prediction</h3>
          {prediction.from_cache && (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">cached</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Horizon selector */}
          <div className="flex bg-gray-100 rounded-md p-0.5">
            {[7, 14, 30].map(h => (
              <button
                key={h}
                onClick={() => setHorizon(h)}
                className={`px-2 py-0.5 text-xs rounded ${
                  horizon === h ? 'bg-white text-indigo-700 shadow-sm font-medium' : 'text-gray-500'
                }`}
              >
                {h}d
              </button>
            ))}
          </div>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
            title="Refresh prediction"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Score & Risk Level */}
      <div className="px-4 py-3 flex items-center gap-4">
        <ScoreGauge score={prediction.churn_score} size={100} />
        <div className="flex-1 space-y-2">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${riskConfig?.bg} ${riskConfig?.color}`}>
            {riskConfig?.icon}
            {riskConfig?.label}
          </div>
          <div className="text-xs text-gray-500 space-y-0.5">
            <div>Confidence: <span className="font-medium text-gray-700">{(prediction.confidence * 100).toFixed(0)}%</span></div>
            <div>Horizon: <span className="font-medium text-gray-700">{horizon} days</span></div>
            {prediction.model_version && (
              <div>Model: <span className="font-medium text-gray-700">v{prediction.model_version}</span></div>
            )}
          </div>
        </div>
      </div>

      {/* Top Factors (collapsible) */}
      <div className="border-t">
        <button
          onClick={() => setShowFactors(!showFactors)}
          className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          {showFactors ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Top Contributing Factors
        </button>
        {showFactors && (
          <div className="px-4 pb-3">
            <TopFactors factors={prediction.top_factors} />
          </div>
        )}
      </div>

      {/* History Trend (collapsible) */}
      <div className="border-t">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          {showHistory ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <BarChart3 className="w-3 h-3" />
          Prediction Trend
        </button>
        {showHistory && historyData?.history && historyData.history.length > 1 && (
          <div className="px-4 pb-3">
            <Sparkline data={historyData.history.map(h => h.churn_score).reverse()} width={280} height={50} />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>{historyData.history.length} predictions ago</span>
              <span>Now</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LivePredictionPanel;
